import React from 'react';
import {
  Tag,
  Info,
  Plus,
  Delete as DeleteIcon,
  FactoryBuilding,
  Clipboard,
  Nutrition,
  ReadBook,
  Link as LinkIcon,
  FileText,
  Picture,
  Video as VideoIcon,
  Music,
  Edit,
  BackgroundColor,
  Minus
} from '@icon-park/react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function getContentIcon(type) {
  const commonProps = { theme: 'outline', size: 18, fill: '#333', strokeWidth: 3 };
  const icons = {
    text: <Edit {...commonProps} />,
    image: <Picture {...commonProps} />,
    video: <VideoIcon {...commonProps} />,
    audio: <Music {...commonProps} />,
    link: <LinkIcon {...commonProps} />,
    attachment: <FileText {...commonProps} />,
    'product-basic': <Clipboard {...commonProps} />,
    'nutrition': <Nutrition {...commonProps} />,
    'usage': <ReadBook {...commonProps} />,
    'related-links': <LinkIcon {...commonProps} />,
    'inspection': <FileText {...commonProps} />,
    'gds-traceability': <FactoryBuilding {...commonProps} />
  };
  return icons[type] || <FileText {...commonProps} />;
}

function getContentName(type) {
  const names = {
    text: '文本',
    image: '图片',
    video: '视频',
    audio: '音频',
    link: '超链接',
    attachment: '附件',
    'product-basic': '产品基础信息',
    'nutrition': '营养成分表',
    'usage': '食用方法',
    'related-links': '相关链接',
    'inspection': '检验报告',
    'gds-traceability': 'GDS追溯模板',
    'divider': '分隔线',
    'background': '背景'
  };
  return names[type] || type;
}

function SortableContentItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function ContentPanel({
  labelMeta,
  setLabelMeta,
  contentItems,
  selectedContentId,
  onSelectContent,
  onDeleteContent,
  onAddContent,
  sensors,
  onDragEnd,
  templateVariant,
}) {
  // 根据模板变体设置默认颜色
  const isStandardTemplate = templateVariant === 'brown-v1';
  const defaultBgColor = isStandardTemplate ? '#5C4A37' : '#2563EB';
  return (
    <div className={`left-panel`}>
      <details className="panel-group" open>
        <summary>主题设置</summary>
        <div style={{ padding: '8px 12px', display: 'grid', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={labelMeta?.headerVisible !== false}
              onChange={(e) => setLabelMeta(m => ({ ...m, headerVisible: e.target.checked }))}
            />
            显示头部标题
          </label>

          {labelMeta?.headerVisible !== false && (
            <div style={{ display: 'grid', gap: 12, padding: '6px 0 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, color: '#111827', fontSize: 13 }}>标题背景色</span>
                    <input
                      type="text"
                      value={labelMeta?.headerStyle?.bgColor || defaultBgColor}
                      onChange={(e) => setLabelMeta(m => ({
                        ...m,
                        headerStyle: {
                          ...(m.headerStyle || {}),
                          bgMode: 'solid',
                          bgColor: e.target.value
                        }
                      }))}
                      style={{
                        width: 96,
                        padding: '6px 8px',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 12,
                        color: '#374151',
                        fontFamily: 'monospace'
                      }}
                    />
                  </div>
                  <label
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 10,
                      overflow: 'hidden',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%)',
                      padding: 10,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.7)'
                    }}
                  >
                    <span style={{ fontSize: 12, color: '#6b7280', marginRight: 8 }}>选择</span>
                    <input
                      type="color"
                      value={labelMeta?.headerStyle?.bgColor || defaultBgColor}
                      onChange={(e) => setLabelMeta(m => ({
                        ...m,
                        headerStyle: {
                          ...(m.headerStyle || {}),
                          bgMode: 'solid',
                          bgColor: e.target.value
                        }
                      }))}
                      style={{
                        width: 48,
                        height: 32,
                        border: 'none',
                        borderRadius: 8,
                        padding: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                      }}
                    />
                  </label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, color: '#111827', fontSize: 13 }}>标题文字色</span>
                    <input
                      type="text"
                      value={labelMeta?.headerStyle?.textColor || '#ffffff'}
                      onChange={(e) => setLabelMeta(m => ({
                        ...m,
                        headerStyle: {
                          ...(m.headerStyle || {}),
                          textColor: e.target.value
                        }
                      }))}
                      style={{
                        width: 96,
                        padding: '6px 8px',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 12,
                        color: '#374151',
                        fontFamily: 'monospace'
                      }}
                    />
                  </div>
                  <label
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 10,
                      overflow: 'hidden',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%)',
                      padding: 10,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.7)'
                    }}
                  >
                    <span style={{ fontSize: 12, color: '#6b7280', marginRight: 8 }}>选择</span>
                    <input
                      type="color"
                      value={labelMeta?.headerStyle?.textColor || '#ffffff'}
                      onChange={(e) => setLabelMeta(m => ({ 
                        ...m, 
                        headerStyle: { 
                          ...(m.headerStyle || {}), 
                          textColor: e.target.value 
                        } 
                      }))}
                      style={{
                        width: 48,
                        height: 32,
                        border: 'none',
                        borderRadius: 8,
                        padding: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ fontWeight: 600, color: '#374151' }}>页脚文案</label>
            <input
              type="text"
              className="toolbar-input"
              placeholder="如：技术支持： 华谱生物"
              value={labelMeta?.footerText || ''}
              onChange={(e) => setLabelMeta(m => ({ ...m, footerText: e.target.value }))}
              style={{ width: '100%', maxWidth: '100%', minWidth: '0', boxSizing: 'border-box' }}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={!!labelMeta?.decorations?.enabled}
              onChange={(e) => setLabelMeta(m => ({ ...m, decorations: { ...(m.decorations || {}), enabled: e.target.checked } }))}
            />
            启用分块装饰线
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={labelMeta?.watermark?.enabled !== false}
              onChange={(e) => setLabelMeta(m => ({ ...m, watermark: { ...(m.watermark || {}), enabled: e.target.checked } }))}
            />
            启用水印
          </label>
        </div>
      </details>

      <details className="panel-group" open>
        <summary>内容列表</summary>
        <div className="content-list-section">
          {contentItems.length === 0 && (
            <div className="content-empty">暂未添加内容模块</div>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={contentItems.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {contentItems.map((item) => (
                <SortableContentItem key={item.id} id={item.id}>
                  <div
                    className={`content-item ${selectedContentId === item.id ? 'selected' : ''}`}
                    onClick={() => onSelectContent(item.id)}
                  >
                    <span className="content-icon">{getContentIcon(item.type)}</span>
                    <span className="content-name">{getContentName(item.type)}</span>
                    {(item.data && item.data.containerStyle && item.data.containerStyle.enabled) ? (
                      <span className="style-flag" title="已启用自定义样式">已设样式</span>
                    ) : null}
                    <button
                      className="content-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteContent(item.id);
                      }}
                    >
                      <DeleteIcon theme="outline" size={14} fill="#333" strokeWidth={3} />
                    </button>
                  </div>
                </SortableContentItem>
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </details>

      <details className="panel-group" open>
        <summary>添加基础内容</summary>
        <div className="add-content-section">
          <div className="add-content-header">
            <span className="add-icon">
              <Plus theme="outline" size={18} fill="#333" strokeWidth={3} />
            </span>
            <span className="add-text">基础内容</span>
            <span className="info-icon" title="添加文本、图片等通用内容">
              <Info theme="outline" size={14} fill="#999" strokeWidth={3} />
            </span>
          </div>
          <div className="content-types">
            <button className="type-btn" onClick={() => onAddContent('text')}>
              <div className="type-icon"><Edit theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">文本</div>
            </button>
            <button className="type-btn" onClick={() => onAddContent('image')}>
              <div className="type-icon"><Picture theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">图片</div>
            </button>
            <button className="type-btn" onClick={() => onAddContent('video')}>
              <div className="type-icon"><VideoIcon theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">视频</div>
            </button>
            <button className="type-btn" onClick={() => onAddContent('audio')}>
              <div className="type-icon"><Music theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">音频</div>
            </button>
            <button className="type-btn" onClick={() => onAddContent('link')}>
              <div className="type-icon"><LinkIcon theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">超链接</div>
            </button>
            <button className="type-btn" onClick={() => onAddContent('attachment')}>
              <div className="type-icon"><FileText theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">附件</div>
            </button>
            <button className="type-btn" onClick={() => onAddContent('divider')}>
              <div className="type-icon"><Minus theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">分隔线</div>
            </button>
            <button className="type-btn" onClick={() => onAddContent('background')}>
              <div className="type-icon"><BackgroundColor theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">背景</div>
            </button>
          </div>
        </div>
      </details>

      <details className="panel-group" open>
        <summary>行业模板</summary>
        <div className="add-content-section template-section">
          <div className="add-content-header">
            <span className="add-icon"><FactoryBuilding theme="outline" size={18} fill="#333" strokeWidth={3} /></span>
            <span className="add-text">业务模板</span>
            <span className="info-icon" title="添加食品行业专业模板">
              <Info theme="outline" size={14} fill="#999" strokeWidth={3} />
            </span>
          </div>
          <div className="content-types">
            <button className="type-btn template-btn" onClick={() => onAddContent('product-basic')}>
              <div className="type-icon"><Clipboard theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">产品信息</div>
            </button>
            <button className="type-btn template-btn" onClick={() => onAddContent('nutrition')}>
              <div className="type-icon"><Nutrition theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">营养成分</div>
            </button>
            <button className="type-btn template-btn" onClick={() => onAddContent('usage')}>
              <div className="type-icon"><ReadBook theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">食用方法</div>
            </button>
            <button className="type-btn template-btn" onClick={() => onAddContent('related-links')}>
              <div className="type-icon"><LinkIcon theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">相关链接</div>
            </button>
            <button className="type-btn template-btn" onClick={() => onAddContent('inspection')}>
              <div className="type-icon"><FileText theme="outline" size={18} fill="#333" strokeWidth={3} /></div>
              <div className="type-name">检验报告</div>
            </button>
            <button className="type-btn template-btn gds-template" onClick={() => onAddContent('gds-traceability')}>
              <div className="type-icon"><FactoryBuilding theme="filled" size={18} fill="#4caf50" strokeWidth={3} /></div>
              <div className="type-name">GDS追溯</div>
            </button>
          </div>
        </div>
      </details>
    </div>
  );
}

export default ContentPanel;
